import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'pwj-marquee-text',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './marquee-text.component.html',
  styleUrl: './marquee-text.component.scss',
})
export class MarqueeTextComponent {
  text = input.required<string>();
  contextActive = input<boolean>(false);

  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  hovered = signal(false);
  effectiveActive = computed(() => this.contextActive() || this.hovered());

  overflow = signal(false);

  private readonly container = viewChild<ElementRef<HTMLElement>>('container');
  private readonly inner = viewChild<ElementRef<HTMLElement>>('inner');

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      const ro = new ResizeObserver(() => this.updateMetrics());
      const c = this.container()?.nativeElement;
      const i = this.inner()?.nativeElement;
      if (c) {
        ro.observe(c);
      }
      if (i) {
        ro.observe(i);
      }
      this.destroyRef.onDestroy(() => ro.disconnect());
      this.updateMetrics();
    });

    effect(() => {
      this.text();
      queueMicrotask(() => this.updateMetrics());
    });
  }

  private updateMetrics(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const c = this.container()?.nativeElement;
    const i = this.inner()?.nativeElement;
    if (!c || !i) {
      return;
    }

    const dist = Math.max(0, i.scrollWidth - c.clientWidth);
    this.overflow.set(dist > 0);

    const rtl = getComputedStyle(c).direction === 'rtl';
    const offset = rtl ? dist : -dist;
    c.style.setProperty('--marquee-distance', `${offset}px`);
  }
}
